// Receipt / invoice generation + the share sheet.
import { useState } from "react";
import { packageExpiryOf, clientToday } from "../domain";
import { fmt, clientPhone, openWhatsApp } from "../lib/format";
import { paidSoFarAsOf, pkgDisplayName } from "../lib/packages";
import { useToast } from "../ui/primitives";

export const generateDocument=(type,payment,client,profile,currency="₹",payments=[])=>{
  const isInvoice=type==="invoice";
  const docNo=isInvoice?`INV-${payment.id?.slice(0,6).toUpperCase()||"000000"}`:`RCP-${payment.id?.slice(0,6).toUpperCase()||"000000"}`;
  const bizName=profile?.businessName||"My Business";
  const bizEmail=profile?.email||"";
  const bizPhone=profile?.phone||"";
  const clientName=client?.name||payment.clientName||"-";
  const amount=(payment.amount||0).toLocaleString("en-IN");
  const paidSoFarNum=paidSoFarAsOf(payments,client,payment);
  const totalPaid=paidSoFarNum.toLocaleString("en-IN");
  const due=Math.max(0,(client?.packagePrice||0)-paidSoFarNum).toLocaleString("en-IN");
  const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${isInvoice?"Invoice":"Receipt"}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Segoe UI',Arial,sans-serif;background:#f8fafc;padding:32px 16px;}
    .card{max-width:520px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);}
    .header{background:linear-gradient(135deg,#0f766e,#0d9488);padding:32px;color:#fff;text-align:center;}
    .logo-circle{width:56px;height:56px;border-radius:14px;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;margin:0 auto 12px;font-size:24px;font-weight:900;}
    .biz-name{font-size:20px;font-weight:800;margin-bottom:4px;}
    .biz-sub{font-size:12px;opacity:0.8;}
    .doc-badge{display:inline-block;background:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.3);padding:4px 14px;border-radius:20px;font-size:11px;font-weight:700;margin-top:10px;}
    .doc-no{font-size:11px;opacity:0.7;margin-top:6px;}
    .body{padding:28px;}
    .section-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;margin-bottom:12px;}
    .row{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid #f3f4f6;font-size:13px;}
    .row:last-child{border-bottom:none;}
    .label{color:#6b7280;}
    .value{font-weight:600;color:#111827;}
    .amount-box{background:linear-gradient(135deg,#f0fdf4,#dcfce7);border-radius:12px;padding:20px;margin:20px 0;text-align:center;}
    .amount-label{font-size:11px;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;}
    .amount-value{font-size:32px;font-weight:900;color:#0f766e;margin-top:6px;}
    ${isInvoice?`.summary{background:#f9fafb;border-radius:10px;padding:16px;margin-top:16px;}.summary-row{display:flex;justify-content:space-between;font-size:12px;padding:4px 0;}.summary-row.total{font-weight:700;font-size:14px;border-top:1px solid #e5e7eb;margin-top:8px;padding-top:8px;}`:""}
    .footer{text-align:center;padding:20px 28px;border-top:1px solid #f3f4f6;font-size:11px;color:#9ca3af;}
    @media print{body{background:#fff;padding:0;}.card{box-shadow:none;border-radius:0;}}
  </style></head><body>
  <div class="card">
    <div class="header">
      <div class="logo-circle">${bizName.charAt(0).toUpperCase()}</div>
      <div class="biz-name">${bizName}</div>
      ${bizEmail||bizPhone?`<div class="biz-sub">${[bizEmail,bizPhone].filter(Boolean).join(" · ")}</div>`:""}
      <div class="doc-badge">✓ ${isInvoice?"Invoice":"Payment Receipt"}</div>
      <div class="doc-no">${docNo}</div>
    </div>
    <div class="body">
      <div class="section-title">Payment Details</div>
      <div class="row"><span class="label">Client</span><span class="value">${clientName}</span></div>
      <div class="row"><span class="label">Date</span><span class="value">${payment.date||"-"}</span></div>
      <div class="row"><span class="label">Mode</span><span class="value">${payment.mode||"-"}</span></div>
      ${payment.note?`<div class="row"><span class="label">Note</span><span class="value">${payment.note}</span></div>`:""}
      ${client?.serviceAvailed?`<div class="row"><span class="label">Service</span><span class="value">${client.serviceAvailed}</span></div>`:""}
      <div class="amount-box">
        <div class="amount-label">Amount ${isInvoice?"Invoiced":"Paid"}</div>
        <div class="amount-value">${currency}${amount}</div>
      </div>
      ${isInvoice?`<div class="summary">
        <div class="summary-row"><span>Total Fee</span><span>${currency}${(client?.packagePrice||0).toLocaleString("en-IN")}</span></div>
        <div class="summary-row"><span>Total Paid</span><span style="color:#16a34a">${currency}${totalPaid}</span></div>
        <div class="summary-row total"><span>Balance Due</span><span style="color:${parseFloat(due)>0?"#dc2626":"#16a34a"}">${currency}${due}</span></div>
      </div>`:""}
    </div>
    <div class="footer">
      <p>Thank you for your ${isInvoice?"business":"payment"}! 🙏</p>
      <p style="margin-top:4px;">Powered by Prabzo · Crafted by Shri Studio</p>
    </div>
  </div>
  <script>window.onload=()=>{window.print();}</script>
  </body></html>`;
  const w=window.open("","_blank","width=600,height=760");
  w?.document.write(html);w?.document.close();
};

export const DocumentOptions = ({ payment, client, profile, currency, onClose, theme, payments }) => {
  const [docType, setDocType] = useState("receipt"); // "receipt" or "invoice"
  const isDark = theme?.dark;
  const p = theme?.primary || "#0f766e";
  const toast = useToast();
  const money = (n) => fmt(n, currency || "₹");

  // Get last 3 payments for this client (excluding current payment)
  const getLastPayments = () => {
    if (!payments) return [];
    return payments
      .filter(p => p.clientId === client?.id && p.id !== payment.id)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 3);
  };

  // Generate text summary for WhatsApp & Copy
  const getSummaryText = () => {
    const isInvoice = docType === "invoice";
    const docId = isInvoice
      ? `INV-${payment.id?.slice(0,6).toUpperCase() || "000000"}`
      : `RCP-${payment.id?.slice(0,6).toUpperCase() || "000000"}`;

    // Business name: businessName OR name + profession
    const bizName = profile?.businessName ||
      (profile?.name ? `${profile.name}${profile?.profession ? ` (${profile.profession})` : ''}` : "Freelancer");

    const clientName = client?.name || payment.clientName || "Client";
    const amount = payment.amount || 0;
    const totalFee = client?.packagePrice || 0;
    const paidSoFar = paidSoFarAsOf(payments, client, payment);
    const balance = Math.max(0, totalFee - paidSoFar);
    const lastPayments = getLastPayments();

    let text = `${isInvoice ? "📄 INVOICE" : "🧾 RECEIPT"} from ${bizName}\n`;
    text += `\n${docId} · ${payment.date || clientToday()}\n`;
    text += `\nClient: ${clientName}`;
    if (client?.mobile) text += `\nPhone: ${client.mobile}`;
    if (client?.serviceAvailed) text += `\nService: ${client.serviceAvailed}`;
    text += `\nAmount: ${money(amount)}`;
    if (payment.mode) text += `\nMode: ${payment.mode}`;

    if (totalFee > 0) {
      text += `\n\nPaid: ${money(paidSoFar)} of ${money(totalFee)}`;
      if (balance > 0) text += `\nBalance Due: ${money(balance)}`;
    }

    // Package details for invoice
    if (isInvoice && client?.package?.type && client.package.type !== "none") {
      const pkgName = pkgDisplayName(client.package);
      text += `\n\nPackage: ${pkgName}`;
      if (client.package.customClasses || client.package.templateSessions) {
        text += `\nSessions: ${client.classesAttended || 0}/${client.package.customClasses || client.package.templateSessions}`;
      }
      // Duration: for custom or template_duration
      if (client.package.customDuration || client.package.templateDurationDays) {
        const duration = client.package.customDuration || `${client.package.templateDurationDays} days`;
        text += `\nDuration: ${duration}`;
      }
      // Due date: calculate expiry if applicable
      const expiry = packageExpiryOf(client);
      if (expiry) text += `\nDue Date: ${expiry}`;
    }

    // Payment history for invoice (last 3)
    if (isInvoice && lastPayments.length > 0) {
      text += `\n\nRecent Payments:`;
      lastPayments.forEach(p => {
        text += `\n  ${p.date} · ${money(p.amount)} · ${p.mode || "-"}`;
      });
    }

    text += `\n\nThank you! 🙏\nPowered by Prabzo`;
    return text;
  };

  // WhatsApp share
  const shareWhatsApp = () => {
    const phone = clientPhone(client);
    if (!phone) { toast("No phone number found for this client.", "warning"); return; }
    openWhatsApp(phone, getSummaryText());
    onClose();
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const text = getSummaryText();
    navigator.clipboard.writeText(text);
    toast("Copied to clipboard! 📋", "success");
    onClose();
  };

  // Generate and print PDF
  const generatePDF = () => {
    generateDocument(docType, payment, client, profile, currency, payments);
    setTimeout(() => onClose(), 500);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-t-2xl overflow-hidden shadow-2xl animate-slide-up"
        style={{ background: isDark ? "#1e293b" : "#fff" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: isDark ? "#334155" : "#f3f4f6" }}>
          <h3 className="font-bold text-sm" style={{ color: isDark ? "#f1f5f9" : "#1f2937" }}>Generate Document</h3>
          <button onClick={onClose} className="p-1 rounded-full" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Document Type Selector */}
        <div className="flex gap-2 p-4 border-b" style={{ borderColor: isDark ? "#334155" : "#f3f4f6" }}>
          {[
            { id: "receipt", label: "🧾 RECEIPT", desc: "Simple payment confirmation" },
            { id: "invoice", label: "📄 INVOICE", desc: "With balance breakdown" }
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setDocType(opt.id)}
              className="flex-1 py-3 rounded-xl text-center transition-all"
              style={{
                background: docType === opt.id ? p : "transparent",
                color: docType === opt.id ? "#fff" : (isDark ? "#94a3b8" : "#6b7280"),
                border: `1px solid ${docType === opt.id ? p : (isDark ? "#334155" : "#e5e7eb")}`
              }}
            >
              <p className="text-xs font-bold">{opt.label}</p>
              <p className="text-[10px] mt-0.5 opacity-70">{opt.desc}</p>
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="p-5">
          <div className="flex gap-3 justify-center">
            <button
              onClick={generatePDF}
              className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all"
              style={{ background: isDark ? "#0f172a" : "#f9fafb", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}` }}
            >
              <span className="text-xl">📄</span>
              <span className="text-xs font-semibold" style={{ color: isDark ? "#f1f5f9" : "#1f2937" }}>PDF</span>
              <span className="text-[10px]" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>Save / Print</span>
            </button>

            <button
              onClick={shareWhatsApp}
              className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all"
              style={{ background: isDark ? "#0f172a" : "#f9fafb", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}` }}
            >
              <span className="text-xl">💬</span>
              <span className="text-xs font-semibold" style={{ color: isDark ? "#f1f5f9" : "#1f2937" }}>WhatsApp</span>
              <span className="text-[10px]" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>Send summary</span>
            </button>

            <button
              onClick={copyToClipboard}
              className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all"
              style={{ background: isDark ? "#0f172a" : "#f9fafb", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}` }}
            >
              <span className="text-xl">📋</span>
              <span className="text-xs font-semibold" style={{ color: isDark ? "#f1f5f9" : "#1f2937" }}>Copy</span>
              <span className="text-[10px]" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>Text summary</span>
            </button>
          </div>

          <p className="text-center text-xs mt-4" style={{ color: isDark ? "#64748b" : "#9ca3af" }}>
            Tap outside to close
          </p>
        </div>
      </div>
    </div>
  );
};
